import { Button, Chip, cn, Dialog, EmptyState, Icon, Menu, Skeleton, TextField } from '@starter/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  type BrandStatus,
  useArchiveSomeBrand,
  useBrands,
  useCreateBrand,
  useUpdateBrand,
} from '../brands/api.ts';
import { EntityCard, EntityGrid } from '../brands/entity-card.tsx';
import { BrandMedia } from '../brands/logo.tsx';
import { useCatalog } from '../catalogs/use-catalog.ts';
import { authClient } from '../lib/auth.ts';
import type { FieldValue } from '../lib/field-value.ts';
import { useFeatureFlag } from '../observability/use-feature-flag.ts';
import { useReadSite } from '../onboarding/api.ts';

// catalog-guard: allow brand_color_roles — `primary`, `secondary` and `text`
// here are a Button variant, a Button variant and a Skeleton shape. The colour
// roles are read from the catalog on the brand's own page, not restated here.

/**
 * "My brands" — the index archetype from `scratchpad/page-index`.
 *
 * The promise the archetype makes, and the reason the header and the filter row
 * are outside every branch below: **a list that is loading, empty or full is
 * the same page in three moods, not three pages.** Nothing above the grid may
 * move as the data arrives.
 */
function Brands() {
  const { status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const brands = useBrands(status);
  const archiveBrand = useArchiveSomeBrand();
  const [creating, setCreating] = useState(false);

  const showArchive = (next: BrandStatus) => navigate({ search: { status: next } });

  return (
    /* gap-8 between the three blocks is the page's whole vertical rhythm. */
    <div className="flex flex-col gap-8">
      {/* One row, 40px: the title and the action that makes another. */}
      <header className="flex min-h-10 flex-wrap items-center gap-4">
        <h1 className="min-w-0 flex-1 font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink">
          My brands
        </h1>
        <Button data-testid="brand-create" onClick={() => setCreating(true)} variant="primary">
          <Icon name="plus" size={16} />
          New brand
        </Button>
      </header>

      {/* The filter sits at the right edge, alone. Nothing is ever on its left. */}
      <div className="flex items-center gap-2">
        <div className="ml-auto">
          <Menu.Root>
            <Menu.Trigger
              render={
                <Chip
                  data-testid="brand-archive-toggle"
                  dropdown
                  selected={status === 'archived'}
                />
              }
            >
              {status === 'archived' ? 'Archived' : 'Active'}
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Positioner align="end">
                <Menu.Popup>
                  <Menu.Item onClick={() => showArchive('active')}>Active</Menu.Item>
                  <Menu.Item
                    data-testid="brand-status-archived"
                    onClick={() => showArchive('archived')}
                  >
                    Archived
                  </Menu.Item>
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
        </div>
      </div>

      {brands.isPending ? (
        <EntityGrid>
          {[0, 1, 2, 3, 4, 5].map((slot) => (
            <div
              className="overflow-hidden rounded-card border-2 border-border-subtle bg-surface-raised"
              key={slot}
            >
              <Skeleton className="aspect-square w-full rounded-none" />
              <div className="flex flex-col gap-1.5 p-4">
                <Skeleton className="w-1/3" shape="text" />
                <Skeleton className="w-4/5" shape="text" />
              </div>
            </div>
          ))}
        </EntityGrid>
      ) : brands.data && brands.data.brands.length > 0 ? (
        <EntityGrid>
          {brands.data.brands.map((brand) => (
            <EntityCard
              actions={
                <Menu.Root>
                  <Menu.Trigger
                    aria-label={`Actions for ${brand.displayName}`}
                    className="flex size-10 cursor-pointer items-center justify-center rounded-md bg-black/50 text-white transition-colors hover:bg-black/65"
                    data-testid="brand-card-actions"
                  >
                    <Icon name="ellipsis" size={24} />
                  </Menu.Trigger>
                  <Menu.Portal>
                    <Menu.Positioner align="end">
                      <Menu.Popup>
                        {status === 'archived' ? null : (
                          <Menu.Item
                            data-testid="brand-card-archive"
                            onClick={() => archiveBrand.mutate(brand.id)}
                          >
                            Archive brand
                          </Menu.Item>
                        )}
                      </Menu.Popup>
                    </Menu.Positioner>
                  </Menu.Portal>
                </Menu.Root>
              }
              key={brand.id}
              media={<BrandMedia name={brand.displayName} uploadId={brand.logoUploadId} />}
              /*
               * Always present, even at zero: dropping the line would change
               * the card's height, and a brand with no products is the common
               * case rather than an exception worth hiding.
               */
              overline={`${brand.products} ${brand.products === 1 ? 'product' : 'products'}`}
              params={{ brandId: brand.id }}
              testId="brand-card"
              title={brand.displayName}
              to="/brands/$brandId"
            />
          ))}
        </EntityGrid>
      ) : (
        /*
         * `inline`, always — the filter chip above is visible, so this emptiness
         * may be the reader's own doing and one click from disappearing. That
         * is the whole test in scratchpad/block-empty.
         */
        <EmptyState
          actions={
            status === 'archived' ? (
              <Button onClick={() => showArchive('active')} variant="secondary">
                Show active
              </Button>
            ) : (
              <Button
                data-testid="brand-create-empty"
                onClick={() => setCreating(true)}
                variant="primary"
              >
                Create a brand
              </Button>
            )
          }
          data-testid="brands-empty"
          description={
            status === 'archived'
              ? 'Nothing has been archived yet.'
              : 'A brand is what the advertising grows out of: its voice, its rules, its products.'
          }
          icon={<Icon name={status === 'archived' ? 'search' : 'store'} />}
          title={status === 'archived' ? 'No archived brands' : 'No brands yet'}
          variant="inline"
        />
      )}

      <CreateBrand
        onDone={(brandId) => navigate({ to: '/brands/$brandId', params: { brandId } })}
        onOpenChange={setCreating}
        open={creating}
      />
    </div>
  );
}

/**
 * Creating a brand: two steps, and only the first one is required.
 *
 * Step one is a name and, optionally, a website. Step two is what a merchant
 * still has in mind while they are thinking about the brand — asking for it
 * later means asking them to come back. Nothing in step two blocks creation:
 * the brand exists after step one's name, and the rest is a PATCH on top of it.
 *
 * Measured in `scratchpad/brand-new`: 1040 wide, a stepper of two bars, and
 * the primary action alone in the footer on the first step, beside a way back
 * on the second. The scrim stays the design system's pale one — the product's
 * is a flat black 50%, and which of those two measurements is current is the
 * open question `scratchpad/block-overlay` carries.
 */
function CreateBrand({
  open,
  onOpenChange,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: (brandId: string) => void;
}) {
  const createBrand = useCreateBrand();
  const updateBrand = useUpdateBrand();
  const verticals = useCatalog('brand_verticals');
  const extras = useFeatureFlag('brand-extra-fields');

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const read = useReadSite();
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [vertical, setVertical] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | undefined>();

  const close = () => {
    onOpenChange(false);
    setStep(1);
    setName('');
    setWebsite('');
    setTagline('');
    setDescription('');
    setVertical(null);
    setProblem(undefined);
  };

  const create = async () => {
    setProblem(undefined);
    try {
      const created = await createBrand.mutateAsync(name.trim());
      const rest: Record<string, FieldValue> = {};
      if (website.trim()) rest.website = website.trim();
      if (tagline.trim()) rest.tagline = tagline.trim();
      if (extras && description.trim()) rest.description = description.trim();
      if (extras && vertical) rest.vertical = vertical;

      if (Object.keys(rest).length > 0) {
        await updateBrand.mutateAsync({ id: created.brand.id, fields: rest });
      }
      close();
      onDone(created.brand.id);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not create the brand');
    }
  };

  const pending = createBrand.isPending || updateBrand.isPending;

  return (
    <Dialog
      className="w-[min(65rem,calc(100vw-2rem))]"
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      open={open}
      title="New brand"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            {[1, 2].map((bar) => (
              <span
                className={cn(
                  'h-1 flex-1 rounded-full',
                  bar <= step ? 'bg-brand-400' : 'bg-brand-100',
                )}
                key={bar}
              />
            ))}
          </div>
          <span className="text-bodysm leading-[16.8px] text-ink-muted">
            {step === 1 ? 'Step 1 of 2 · Name and source' : 'Step 2 of 2 · Details'}
          </span>
        </div>

        {step === 1 ? (
          <>
            <TextField
              data-testid="brand-name"
              label="Business name"
              onChange={(event) => setName(event.currentTarget.value)}
              placeholder="Morning Coffee"
              required
              value={name}
            />
            {/*
              «Read the site» is the original's own control, inside the field
              rather than beside it — which is why this field is 54 tall and
              radius 16 while a plain one is 48 and 8 (`scratchpad/brand-new`).
              What it reads is the address, not the page: this build has no
              crawler and says so (`apps/api/src/domain/onboarding.ts`).
            */}
            <TextField
              action={
                <Button
                  data-testid="brand-read-site"
                  disabled={website.trim() === '' || read.isPending}
                  onClick={async () => {
                    const answer = await read.mutateAsync({ url: website });
                    if (!name.trim() && answer.draft.name) setName(answer.draft.name);
                  }}
                  size="sm"
                  variant="secondary"
                >
                  {read.isPending ? 'Reading…' : 'Read it'}
                </Button>
              }
              data-testid="brand-website"
              description="Optional. Reading it takes a name from the address — nothing opens the page."
              label="Website"
              onChange={(event) => setWebsite(event.currentTarget.value)}
              placeholder="https://example.com"
              value={website}
            />
          </>
        ) : (
          <>
            <TextField
              data-testid="brand-tagline"
              label="Tagline"
              onChange={(event) => setTagline(event.currentTarget.value)}
              placeholder="Roasted the morning it ships"
              value={tagline}
            />
            {extras ? (
              <>
                <TextField
                  data-testid="brand-description"
                  label="What it does"
                  onChange={(event) => setDescription(event.currentTarget.value)}
                  placeholder="We roast coffee in small batches and deliver it within 24 hours"
                  value={description}
                />
                <div className="flex flex-col gap-1.5">
                  <span className="text-bodysm leading-[16.8px] font-semibold text-ink">
                    Vertical
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {verticals.items.map((item) => (
                      <Chip
                        data-testid={`brand-vertical-${item.key}`}
                        key={item.key}
                        onClick={() => setVertical(vertical === item.key ? null : item.key)}
                        selected={vertical === item.key}
                      >
                        {verticals.label(item.key)}
                      </Chip>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </>
        )}

        {problem ? (
          <p className="text-sm text-danger-text" role="alert">
            {problem}
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          {step === 2 ? (
            <Button onClick={() => setStep(1)} variant="secondary">
              Back
            </Button>
          ) : null}
          {step === 1 ? (
            <Button
              data-testid="brand-next"
              disabled={name.trim() === ''}
              onClick={() => setStep(2)}
              variant="primary"
            >
              Next
            </Button>
          ) : (
            <Button data-testid="brand-save" disabled={pending} onClick={create} variant="primary">
              {pending ? 'Creating…' : 'Create brand'}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export const Route = createFileRoute('/brands/')({
  validateSearch: z.object({
    status: z.enum(['active', 'archived']).default('active'),
  }),
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: Brands,
});
